import { useImageCacheStore } from '@renderer/stores/imageCacheStore'
import { legacyCdnTwin, rewriteLegacyCdnUrl } from './legacyCdnHost'

/**
 * Checks if a URL should be cached
 * Skips caching for data URLs, blob URLs, and relative paths
 * Caches HTTP/HTTPS URLs and absolute paths (public folder assets)
 */
const shouldCache = (url: string): boolean => {
  if (!url) return false
  // Skip data URLs
  if (url.startsWith('data:')) return false
  // Skip blob URLs
  if (url.startsWith('blob:')) return false
  // Cache HTTP/HTTPS URLs
  if (url.startsWith('http://') || url.startsWith('https://')) return true
  // Cache absolute paths (public folder assets like /assets/...)
  if (url.startsWith('/')) return true
  return false
}

/**
 * Gets a cached image URL or fetches and caches it
 * @param url - The image URL to cache/fetch
 * @param options - Optional configuration
 * @param options.expirationHours - Override default expiration (24 hours)
 * @returns Promise resolving to blob URL (cached or newly created)
 */
export const getCachedImageUrl = async (
  requestedUrl: string,
  options?: { expirationHours?: number }
): Promise<string> => {
  // If URL shouldn't be cached, return as-is
  if (!shouldCache(requestedUrl)) {
    return requestedUrl
  }

  // workers.dev is blocked on some venue networks; load from the custom domain instead
  // (see legacyCdnHost). A copy cached under the old host still counts.
  const url = rewriteLegacyCdnUrl(requestedUrl)
  const twin = legacyCdnTwin(url)

  const store = useImageCacheStore.getState()
  const expirationHours = options?.expirationHours ?? store.defaultExpirationHours

  // Check cache first (async now)
  const cachedUrl =
    (await store.getCachedUrl(url, expirationHours)) ??
    (twin ? await store.getCachedUrl(twin, expirationHours) : undefined)
  if (cachedUrl) {
    return cachedUrl
  }

  // Check for expired cache (for offline fallback)
  const expiredCache = store.cache.get(url) ?? (twin ? store.cache.get(twin) : undefined)

  // For local paths, ensure we have the full URL if needed
  // In Electron, local paths might need to be resolved differently
  let fetchUrl = url
  if (url.startsWith('/') && typeof window !== 'undefined') {
    // For local paths, use the current origin
    fetchUrl = window.location.origin + url
  }

  try {
    // Fetch the image
    // For local paths (public folder assets), use 'same-origin' mode
    // For external URLs, use 'cors' mode
    const isLocalPath = url.startsWith('/')
    const response = await fetch(fetchUrl, {
      mode: isLocalPath ? 'same-origin' : 'cors',
      cache: 'no-cache'
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.statusText}`)
    }

    const blob = await response.blob()
    const blobUrl = URL.createObjectURL(blob)

    // Store in cache (with blob for IndexedDB persistence)
    await store.setCachedUrl(url, blobUrl, blob)

    return blobUrl
  } catch (error) {
    console.warn('Failed to fetch image, attempting to use expired cache if available:', error)

    // If fetch failed but we have expired cache, use it (better than nothing offline)
    if (expiredCache) {
      console.warn('Using expired cache for offline support:', url)
      // Re-add to cache with new timestamp (treat as valid for offline)
      // Try to get blob from IndexedDB for persistence
      try {
        const { getCachedBlob } = await import('@renderer/utils/assetCacheDB')
        const blob = await getCachedBlob(url)
        await store.setCachedUrl(url, expiredCache.blobUrl, blob || undefined)
      } catch {
        await store.setCachedUrl(url, expiredCache.blobUrl)
      }
      return expiredCache.blobUrl
    }

    // If no cache exists and fetch failed, return original URL
    console.warn('No cache available, returning original URL:', url)
    return url
  }
}

/** Classify photo URL for upload / fetch diagnostics (no PII). */
export function photoUrlSchemeForDiagnostics(url: string | undefined | null): string {
  if (!url || !url.trim()) return 'empty'
  const raw = url.startsWith('mirror:') ? url.slice(7) : url
  if (raw.startsWith('blob:')) return 'blob'
  if (raw.startsWith('data:')) return 'data'
  if (raw.startsWith('https:')) return 'https'
  if (raw.startsWith('http:')) return 'http'
  if (raw.startsWith('/')) return 'path'
  return 'other'
}

/** Bundled context when individual photo upload fails (ops / repro). */
export function summarizePhotoUrlsForUploadDebug(items: { photoUrl?: string }[]): Record<string, unknown> {
  return {
    photoUrlSchemes: items.map((i) => photoUrlSchemeForDiagnostics(i.photoUrl)),
    photoCount: items.length,
    viteApiUrl:
      typeof process !== 'undefined' && process.env?.VITE_API_URL
        ? String(process.env.VITE_API_URL)
        : ''
  }
}
